import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import filesAPI from '../api/files';
import mentorsAPI, { Mentor, MentorId } from '../api/mentors';
import venturesAPI from '../api/ventures';
import { Venture } from '../api/ventures/types/Venture';
import { SnackMessage } from '../components/common';
import { massAsyncRequest } from '../utils/api';

export interface MentorsContextProps {
  mentors: Mentor[];
  mentorsLogos: { [x: string]: string | null };
  selectedMentorIds: Mentor['id'][];
  assignTargetMentor: Mentor | null;
  isAssigningMentor: { [x: string]: boolean };
  isSendingProfileUpdate: boolean;
  profileUpdateTargetMentorId: MentorId | null;
  loadedVenturesById: {
    [x: string]: Venture[];
  };
  setMentors: Dispatch<SetStateAction<Mentor[]>>;
  setSelectedMentorIds: Dispatch<SetStateAction<Mentor['id'][]>>;
  setIsSendingProfileUpdate: Dispatch<SetStateAction<boolean>>;
  handleAssingSuccessForm: (
    mentorId: Mentor['id'],
    handleClose: () => void,
  ) => (data: {
    assigned: string[];
    removed: string[];
    all: Venture[];
  }) => Promise<void>;
  handleVenturesLoaded: (
    mentorId: Mentor['id'],
  ) => (loadedVentures: Venture[]) => void;
  setProfileUpdateTargetMentorId: (mentorId: MentorId | null) => void;
  setAssignTargetMentor: (mentor: Mentor | null) => void;
  handleSendMentorUpdateProfiles: (onClose: () => {}) => Promise<void>;
}

export const MentorsContext = createContext<MentorsContextProps>({
  mentors: [],
  mentorsLogos: {},
  selectedMentorIds: [],
  assignTargetMentor: null,
  isAssigningMentor: {},
  isSendingProfileUpdate: false,
  profileUpdateTargetMentorId: null,
  loadedVenturesById: {},
  setMentors: () => {},
  setSelectedMentorIds: () => {},
  setIsSendingProfileUpdate: () => {},
  handleAssingSuccessForm:
    () =>
    // eslint-disable-next-line no-empty-pattern
    async ({ assigned: [], removed: [], all: [] }) => {},
  handleVenturesLoaded: () => () => {},
  setProfileUpdateTargetMentorId: () => {},
  setAssignTargetMentor: () => {},
  handleSendMentorUpdateProfiles: async () => {},
});

interface MentorsProviderProps {
  children: ReactNode;
}

export const MentorsProvider = ({ children }: MentorsProviderProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [mentorsLogos, setMentorsLogos] = useState<{
    [x: string]: string | null;
  }>({});
  const [assignTargetMentor, setAssignTargetMentor] = useState<Mentor | null>(
    null,
  );
  const [isAssigningMentor, setIsAssigningMentor] = useState<{
    [x: string]: boolean;
  }>({});
  const [profileUpdateTargetMentorId, setProfileUpdateTargetMentorId] =
    useState<MentorId | null>(null);
  const [isSendingProfileUpdate, setIsSendingProfileUpdate] = useState(false);
  const [loadedVenturesById, setLoadedVenturesById] = useState<{
    [x: string]: Venture[];
  }>({});
  const [selectedMentorIds, setSelectedMentorIds] = useState<Mentor['id'][]>(
    [],
  );

  const handleVenturesLoaded = useCallback(
    (mentorId: Mentor['id']) => (loadedVentures: Venture[]) => {
      setMentors((prevMentors) =>
        prevMentors.map((mentor) =>
          mentor.id === mentorId
            ? { ...mentor, assigned: loadedVentures.length }
            : mentor,
        ),
      );
      setLoadedVenturesById((prevState) => ({
        ...prevState,
        [mentorId]: loadedVentures,
      }));
    },
    [],
  );

  const handleAssingSuccessForm = useCallback(
    (mentorId: Mentor['id'], handleModalClose: () => any) =>
      async ({
        assigned,
        removed,
        all,
      }: {
        assigned: string[];
        removed: string[];
        all: Venture[];
      }) => {
        if (!assigned.length && !removed.length) {
          return handleModalClose();
        }

        try {
          setIsAssigningMentor((prev) => ({ ...prev, [mentorId]: true }));
          const assignedRequests = assigned.map(
            (ventureId) => () => venturesAPI.assignMentor(ventureId, mentorId),
          );
          const removedRequests = removed.map(
            (ventureId) => () =>
              venturesAPI.removeMentorAssignment(ventureId, mentorId),
          );
          await Promise.all([
            massAsyncRequest(assignedRequests),
            massAsyncRequest(removedRequests),
          ]);
          handleVenturesLoaded(mentorId)(all);
          handleModalClose();
          setIsAssigningMentor((prev) => ({ ...prev, [mentorId]: false }));
        } catch (e: any) {
          console.log('error', 'e');
        }
      },
    [handleVenturesLoaded],
  );

  const handleSendMentorUpdateProfiles = useCallback(
    async (onClose: () => {}) => {
      try {
        if (!profileUpdateTargetMentorId?.length) {
          return;
        }

        setIsSendingProfileUpdate(true);
        await mentorsAPI.sendMentorUpdateInvites([profileUpdateTargetMentorId]);
        enqueueSnackbar('The profile update requests were successfully sent', {
          variant: 'success',
        });
        setIsSendingProfileUpdate(false);
        onClose();
        setProfileUpdateTargetMentorId(null);
      } catch (e) {
        const messageError = (e as AxiosError).response?.data?.message;

        setIsSendingProfileUpdate(false);
        enqueueSnackbar(
          'An error occurred while sending profile update request. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        throw e;
      }
    },
    [enqueueSnackbar, profileUpdateTargetMentorId],
  );

  const loadMentorsLogos = useCallback(async (mentorsList: Mentor[]) => {
    try {
      const mentorImages = mentorsList.map((mentor: Mentor) => ({
        id: mentor.id,
        logo: mentor.logo,
      }));
      setMentorsLogos(() => {
        const sources = mentorImages.reduce((res, mentor) => {
          res[mentor.id] = mentor.logo ? '' : null;
          return res;
        }, {} as { [x: string]: string | null });
        return sources;
      });
      const shouldBeLoaded = mentorImages.filter(
        (mentorImage) => !!mentorImage.logo,
      );
      const logos = shouldBeLoaded.map((mentorImage) => mentorImage.logo);
      if (!logos.length) {
        return false;
      }
      const loadedLogos = await filesAPI.getFileLogos(logos as string[]);

      const nextState = shouldBeLoaded.reduce(
        (res, mentorImage, mentorImageIndex) => {
          res[mentorImage.id] = loadedLogos[mentorImageIndex];
          return res;
        },
        {} as { [x: string]: string },
      );
      return setMentorsLogos((prevMentors) => ({
        ...prevMentors,
        ...nextState,
      }));
    } catch (e: any) {
      console.log('loadMentorsLogos error');
      return false;
    }
  }, []);

  useEffect(() => {
    if (mentors.length) {
      loadMentorsLogos(mentors);
    }
  }, [loadMentorsLogos, mentors]);

  const contextValue: MentorsContextProps = {
    mentors,
    mentorsLogos,
    selectedMentorIds,
    assignTargetMentor,
    isAssigningMentor,
    isSendingProfileUpdate,
    profileUpdateTargetMentorId,
    loadedVenturesById,
    setMentors,
    setSelectedMentorIds,
    setIsSendingProfileUpdate,
    handleAssingSuccessForm,
    handleVenturesLoaded,
    setProfileUpdateTargetMentorId,
    setAssignTargetMentor,
    handleSendMentorUpdateProfiles,
  };

  return (
    <MentorsContext.Provider value={contextValue}>
      {children}
    </MentorsContext.Provider>
  );
};

export const useMentorsContext = () => {
  const context = useContext(MentorsContext);
  if (!context) {
    throw new Error('useMentorsContext must be used within a MentorsProvider');
  }
  return context;
};
